import { useState } from "react";
import axios from "axios";
import { API } from "../contexts/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Download, FileText } from "lucide-react";
import { toast } from "sonner";

const MONTHS_ES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const formatCurrency = (v) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(v);
const currentYear = new Date().getFullYear();
const years = Array.from({ length: 10 }, (_, i) => String(currentYear - 5 + i));
const QUARTER_NAMES = { 1: "1er Trimestre (Ene-Mar)", 2: "2o Trimestre (Abr-Jun)", 3: "3er Trimestre (Jul-Sep)", 4: "4o Trimestre (Oct-Dic)" };

export default function VatReportsPage() {
  const [tab, setTab] = useState("quarterly");
  const [qYear, setQYear] = useState(String(currentYear));
  const [qQuarter, setQQuarter] = useState("1");
  const [qData, setQData] = useState(null);
  const [aYear, setAYear] = useState(String(currentYear));
  const [aData, setAData] = useState(null);
  const [loadingQ, setLoadingQ] = useState(false);
  const [loadingA, setLoadingA] = useState(false);

  const loadQuarterly = async () => {
    setLoadingQ(true);
    try {
      const { data } = await axios.get(`${API}/vat/quarterly`, { params: { year: parseInt(qYear), quarter: parseInt(qQuarter) } });
      setQData(data);
    } catch { toast.error("Error cargando informe"); }
    finally { setLoadingQ(false); }
  };

  const loadAnnual = async () => {
    setLoadingA(true);
    try {
      const { data } = await axios.get(`${API}/vat/annual`, { params: { year: parseInt(aYear) } });
      setAData(data);
    } catch { toast.error("Error cargando informe"); }
    finally { setLoadingA(false); }
  };

  const downloadQuarterlyPdf = async () => {
    try {
      const response = await axios.get(`${API}/vat/quarterly/pdf`, { params: { year: parseInt(qYear), quarter: parseInt(qQuarter) }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `IVA_T${qQuarter}_${qYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Error descargando PDF"); }
  };

  const downloadAnnualPdf = async () => {
    try {
      const response = await axios.get(`${API}/vat/annual/pdf`, { params: { year: parseInt(aYear) }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `IVA_Anual_${aYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Error descargando PDF"); }
  };

  return (
    <div data-testid="vat-reports-page">
      <div className="mb-8">
        <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-gray-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Informes de IVA</h2>
        <p className="text-sm text-gray-500 mt-1">Resúmenes trimestrales y anuales</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6" data-testid="vat-tabs">
          <TabsTrigger value="quarterly" data-testid="quarterly-tab">Trimestral</TabsTrigger>
          <TabsTrigger value="annual" data-testid="annual-tab">Anual</TabsTrigger>
        </TabsList>

        <TabsContent value="quarterly">
          <div className="bg-white border border-gray-200 rounded-sm p-6">
            <div className="flex items-end gap-4 mb-6">
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block uppercase tracking-wider">Año</label>
                <Select value={qYear} onValueChange={setQYear}>
                  <SelectTrigger className="w-28 rounded-sm" data-testid="q-year-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block uppercase tracking-wider">Trimestre</label>
                <Select value={qQuarter} onValueChange={setQQuarter}>
                  <SelectTrigger className="w-52 rounded-sm" data-testid="q-quarter-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4].map(q => <SelectItem key={q} value={String(q)}>{QUARTER_NAMES[q]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <button onClick={loadQuarterly} disabled={loadingQ} className="bg-[#002FA7] text-white hover:bg-[#00227B] rounded-sm px-5 py-2 text-sm font-semibold transition-all flex items-center gap-2 h-9 disabled:opacity-50" data-testid="load-quarterly-button">
                <FileText className="w-4 h-4" /> {loadingQ ? "Cargando..." : "Consultar"}
              </button>
              {qData && (
                <button onClick={downloadQuarterlyPdf} className="bg-white text-gray-900 border border-gray-200 hover:bg-gray-50 rounded-sm px-4 py-2 text-sm font-semibold transition-all flex items-center gap-2 h-9" data-testid="download-quarterly-pdf">
                  <Download className="w-4 h-4" /> PDF
                </button>
              )}
            </div>

            {qData && (
              <div data-testid="quarterly-results">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">N.º Factura</TableHead>
                      <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Fecha</TableHead>
                      <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Inquilino</TableHead>
                      <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold text-right">Base Imponible</TableHead>
                      <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold text-right">IVA (21%)</TableHead>
                      <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {qData.invoices.map((inv) => (
                      <TableRow key={inv.id} className="hover:bg-gray-50 transition-colors">
                        <TableCell className="text-sm font-medium text-gray-900 py-3 px-4">{inv.invoice_number}</TableCell>
                        <TableCell className="text-sm text-gray-600 py-3 px-4">01/{String(inv.month).padStart(2, '0')}/{inv.year}</TableCell>
                        <TableCell className="text-sm text-gray-900 py-3 px-4">{inv.tenant_name}</TableCell>
                        <TableCell className="text-sm text-gray-900 py-3 px-4 text-right">{formatCurrency(inv.base_amount)}</TableCell>
                        <TableCell className="text-sm text-gray-600 py-3 px-4 text-right">{formatCurrency(inv.vat_amount)}</TableCell>
                        <TableCell className="text-sm font-medium text-gray-900 py-3 px-4 text-right">{formatCurrency(inv.total_amount)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-gray-50 font-bold">
                      <TableCell colSpan={3} className="text-sm font-bold text-gray-900 py-4 px-4 text-right">TOTALES</TableCell>
                      <TableCell className="text-sm font-bold text-gray-900 py-4 px-4 text-right">{formatCurrency(qData.total_base)}</TableCell>
                      <TableCell className="text-sm font-bold text-[#002FA7] py-4 px-4 text-right">{formatCurrency(qData.total_vat)}</TableCell>
                      <TableCell className="text-sm font-bold text-gray-900 py-4 px-4 text-right">{formatCurrency(qData.total)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-sm">
                  <p className="text-sm font-semibold text-[#002FA7]">IVA a ingresar este trimestre: {formatCurrency(qData.total_vat)}</p>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="annual">
          <div className="bg-white border border-gray-200 rounded-sm p-6">
            <div className="flex items-end gap-4 mb-6">
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block uppercase tracking-wider">Año</label>
                <Select value={aYear} onValueChange={setAYear}>
                  <SelectTrigger className="w-28 rounded-sm" data-testid="a-year-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <button onClick={loadAnnual} disabled={loadingA} className="bg-[#002FA7] text-white hover:bg-[#00227B] rounded-sm px-5 py-2 text-sm font-semibold transition-all flex items-center gap-2 h-9 disabled:opacity-50" data-testid="load-annual-button">
                <FileText className="w-4 h-4" /> {loadingA ? "Cargando..." : "Consultar"}
              </button>
              {aData && (
                <button onClick={downloadAnnualPdf} className="bg-white text-gray-900 border border-gray-200 hover:bg-gray-50 rounded-sm px-4 py-2 text-sm font-semibold transition-all flex items-center gap-2 h-9" data-testid="download-annual-pdf">
                  <Download className="w-4 h-4" /> PDF
                </button>
              )}
            </div>

            {aData && (
              <div data-testid="annual-results">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Trimestre</TableHead>
                      <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold text-right">N.º Facturas</TableHead>
                      <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold text-right">Base Imponible</TableHead>
                      <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold text-right">IVA Repercutido</TableHead>
                      <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {aData.quarters.map((q) => (
                      <TableRow key={q.quarter} className="hover:bg-gray-50 transition-colors">
                        <TableCell className="text-sm font-medium text-gray-900 py-4 px-4">{QUARTER_NAMES[q.quarter]}</TableCell>
                        <TableCell className="text-sm text-gray-600 py-4 px-4 text-right">{q.invoice_count}</TableCell>
                        <TableCell className="text-sm text-gray-900 py-4 px-4 text-right">{formatCurrency(q.base)}</TableCell>
                        <TableCell className="text-sm text-gray-600 py-4 px-4 text-right">{formatCurrency(q.vat)}</TableCell>
                        <TableCell className="text-sm font-medium text-gray-900 py-4 px-4 text-right">{formatCurrency(q.total)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-gray-50 font-bold">
                      <TableCell className="text-sm font-bold text-gray-900 py-4 px-4">TOTAL ANUAL</TableCell>
                      <TableCell className="py-4 px-4" />
                      <TableCell className="text-sm font-bold text-gray-900 py-4 px-4 text-right">{formatCurrency(aData.total_base)}</TableCell>
                      <TableCell className="text-sm font-bold text-[#002FA7] py-4 px-4 text-right">{formatCurrency(aData.total_vat)}</TableCell>
                      <TableCell className="text-sm font-bold text-gray-900 py-4 px-4 text-right">{formatCurrency(aData.total)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-sm">
                  <p className="text-sm font-semibold text-[#002FA7]">IVA total repercutido en {aData.year}: {formatCurrency(aData.total_vat)}</p>
                </div>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
